import { Button, Card, Progress, Space, Tag, Typography } from 'antd';
import { ClockCircleOutlined, ControlOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { formatNumber, formatDateTime } from '@/lib/format';
import { balanceLevel, type BalanceStatus, type SmsKpi } from './hooks';

const { Text } = Typography;

const LEVEL_COLOR: Record<'ok' | 'warn' | 'blocked', string> = {
  ok: 'success',
  warn: 'warning',
  blocked: 'error',
};

// Максимум шкалы прогресса баланса. В прототипе — 90 000 ₸ как «полная» отметка.
const BALANCE_SCALE = 90_000;

interface BalanceCardProps {
  balance: BalanceStatus;
  kpi: SmsKpi;
  onConfigureThreshold: () => void;
  onRefresh: () => void;
  refreshing: boolean;
}

// Карточка баланса SMSC (M5.4). Сумма ₸ + статус ОК/предупреждение/ЗАБЛОКИРОВАНО,
// шкала «0 … порог», сводные KPI (месяц/сегодня/оптик) и время последней
// синхронизации из `checked_at`.
export function BalanceCard({
  balance,
  kpi,
  onConfigureThreshold,
  onRefresh,
  refreshing,
}: BalanceCardProps): JSX.Element {
  const { t } = useTranslation();
  const level = balanceLevel(balance);
  const color = LEVEL_COLOR[level];

  const balancePct =
    balance.balance_kzt === null
      ? 0
      : Math.min(100, Math.round((balance.balance_kzt / BALANCE_SCALE) * 100));
  const thresholdPct = Math.min(100, Math.round((balance.threshold_kzt / BALANCE_SCALE) * 100));

  return (
    <Card variant="outlined" data-testid="balance-card">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text
            type="secondary"
            style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' }}
          >
            {t('platform.balance.title')}
          </Text>
          <Button
            size="small"
            icon={<ControlOutlined />}
            onClick={onConfigureThreshold}
            title={t('platform.balance.configure_threshold')}
            data-testid="configure-threshold-btn"
          />
        </Space>

        <div>
          <Text
            strong
            style={{ fontSize: 40, lineHeight: 1, letterSpacing: '-0.02em' }}
            className="sl-mono"
            data-testid="balance-amount"
          >
            {balance.balance_kzt === null
              ? t('platform.balance.unknown')
              : `${formatNumber(balance.balance_kzt)} ₸`}
          </Text>
        </div>

        <Tag
          color={color}
          data-testid="balance-status"
          style={{ fontSize: 12, padding: '4px 12px' }}
        >
          {t(`platform.balance.level.${level}`)}
        </Tag>

        <div>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              0 ₸
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('platform.balance.threshold', { value: formatNumber(balance.threshold_kzt) })}
            </Text>
          </Space>
          <div style={{ position: 'relative' }}>
            <Progress
              percent={balancePct}
              showInfo={false}
              size="small"
              status={level === 'blocked' ? 'exception' : 'normal'}
              strokeColor={
                level === 'warn' ? '#e0a23a' : level === 'blocked' ? '#d8556b' : '#2dbdb8'
              }
            />
            {/* Метка порога на шкале. */}
            <div
              style={{
                position: 'absolute',
                top: -2,
                bottom: 2,
                width: 2,
                background: 'var(--deep-trust-blue, #0e3559)',
                left: `${thresholdPct}%`,
              }}
            />
          </div>
        </div>

        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <KpiRow
            label={t('platform.kpi.month')}
            value={`${formatNumber(kpi.sms_this_month)} SMS`}
          />
          <KpiRow label={t('platform.kpi.today')} value={formatNumber(kpi.sms_today)} />
          <KpiRow label={t('platform.kpi.active_optics')} value={formatNumber(kpi.active_optics)} />
        </Space>

        <Space size={7} style={{ borderTop: '1px solid var(--hairline)', paddingTop: 12 }}>
          <ClockCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }} />
          <Text type="secondary" style={{ fontSize: 12 }} data-testid="balance-checked-at">
            {balance.checked_at
              ? t('platform.balance.synced_at', { when: formatDateTime(balance.checked_at) })
              : t('platform.balance.never_synced')}
          </Text>
        </Space>

        <Button
          type="primary"
          icon={<ReloadOutlined />}
          loading={refreshing}
          onClick={onRefresh}
          block
        >
          {t('platform.balance.refresh')}
        </Button>
      </Space>
    </Card>
  );
}

function KpiRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Space style={{ width: '100%', justifyContent: 'space-between' }} align="baseline">
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>
      <Text strong className="sl-mono" style={{ fontSize: 14 }}>
        {value}
      </Text>
    </Space>
  );
}
